import { prisma } from "@/lib/db";
import {
  buildSessionQueue,
  checkFocusMastery,
  getSetCounts,
  initializeFocusSet,
  calculatePCorrectFromLogs,
  getBoxStatus,
  getUserPhase,
  checkAndPerformGraduation,
  RETRY_DELAY_PROBLEMS,
  EASY_THRESHOLD,
} from "@/lib/scheduler";
import type { FocusSetStatus, NextProblemResult, SessionProblem } from "@/lib/scheduler";
import { NextRequest, NextResponse } from "next/server";

export async function POST(request: NextRequest) {
  try {
    const body = await request.json();
    const {
      userId,
      sessionId,
      recentWrong = [],
      recentShown = [],
      consecutiveWrong = 0,
    } = body;

    if (!userId) {
      return NextResponse.json({ error: "userId required" }, { status: 400 });
    }

    const now = new Date();

    // Ensure Focus set is initialized
    await initializeFocusSet(userId);

    // Get user phase info
    const { currentPhase } = await getUserPhase(userId);

    // Get current set counts (legacy compatibility)
    const counts = await getSetCounts(userId);
    const boxStatus = await getBoxStatus(userId);

    const focusStatus: FocusSetStatus = {
      ...counts,
      masteryPercent: counts.focusCount > 0
        ? Math.round((counts.focusCorrectCount / counts.focusCount) * 100)
        : 0,
      mastered: false,
      boxStatus,
    };

    // Phase 1: Check for graduation (enough cards in Box 3)
    if (currentPhase === 1) {
      const { mastered } = await checkFocusMastery(userId);
      if (mastered) {
        // Perform graduation
        const graduationResult = await checkAndPerformGraduation(userId);

        if (graduationResult.graduated) {
          return NextResponse.json({
            problem: null,
            done: false,
            mastered: true,
            graduated: true,
            message: `${graduationResult.graduatedCount} cards graduated! ${graduationResult.newCardsAdded} new cards added.`,
            focusStatus: { ...focusStatus, mastered: true },
          } as NextProblemResult);
        }
      }
    }

    // Check if there are any problems left
    if (counts.focusCount === 0 && counts.newCount === 0) {
      return NextResponse.json({
        problem: null,
        done: true,
        mastered: false,
        message: "No more problems available",
        focusStatus,
      } as NextProblemResult);
    }

    // Handle retry queue first (if enough problems have passed)
    if (recentWrong.length > 0) {
      const recentSet = new Set(recentShown.slice(-RETRY_DELAY_PROBLEMS));
      const retryId = recentWrong.find((id: string) => !recentSet.has(id));

      if (retryId) {
        // Check if we need an easy problem instead
        const needsEasy = consecutiveWrong >= 2;

        if (needsEasy) {
          const easyProblem = await findEasyProblem(userId, recentShown, now);
          if (easyProblem) {
            return NextResponse.json({
              problem: easyProblem,
              done: false,
              mastered: false,
              focusStatus,
            } as NextProblemResult);
          }
        }

        // Serve the retry
        const problem = await prisma.problem.findUnique({
          where: { id: retryId },
          include: {
            userProblemStates: {
              where: { userId },
            },
          },
        });

        if (problem) {
          const logs = await getRecentLogs(userId, problem.id);
          const pCorrect = calculatePCorrectFromLogs(logs, now);
          const box = problem.userProblemStates[0]?.box ?? 0;

          return NextResponse.json({
            problem: {
              problemId: problem.id,
              problemText: problem.problemText,
              groupId: problem.groupId,
              type: "retry",
              pCorrect,
              box,
            } as SessionProblem,
            done: false,
            mastered: false,
            focusStatus,
          } as NextProblemResult);
        }
      }
    }

    // If 2+ consecutive wrong, prioritize easy problem
    if (consecutiveWrong >= 2) {
      const easyProblem = await findEasyProblem(userId, recentShown, now);
      if (easyProblem) {
        return NextResponse.json({
          problem: easyProblem,
          done: false,
          mastered: false,
          focusStatus,
        } as NextProblemResult);
      }
    }

    // Build session queue and get next problem
    const queue = await buildSessionQueue(userId);

    // Find first problem not recently shown
    const recentSet = new Set(recentShown.slice(-3));
    const nextProblem = queue.find(p => !recentSet.has(p.problemId));

    if (!nextProblem) {
      const problem = queue[0];
      if (problem) {
        return NextResponse.json({
          problem,
          done: false,
          mastered: false,
          focusStatus,
        } as NextProblemResult);
      }

      return NextResponse.json({
        problem: null,
        done: true,
        mastered: false,
        message: "No problems available",
        focusStatus,
      } as NextProblemResult);
    }

    return NextResponse.json({
      problem: nextProblem,
      done: false,
      mastered: false,
      focusStatus,
    } as NextProblemResult);
  } catch (error) {
    console.error("Error getting next problem:", error);
    return NextResponse.json(
      { error: error instanceof Error ? error.message : "Failed to get next problem" },
      { status: 500 }
    );
  }
}

/**
 * Find an easy problem (high box or high p_correct)
 */
async function findEasyProblem(
  userId: string,
  recentShown: string[],
  now: Date
): Promise<SessionProblem | null> {
  const recentSet = new Set(recentShown.slice(-3));

  // Get problems in boxes 2-3 (more stable) or graduated
  const states = await prisma.userProblemState.findMany({
    where: {
      userId,
      box: { gte: 2 },
      problemId: { notIn: Array.from(recentSet) },
    },
    include: { problem: true },
    orderBy: { box: "desc" },
    take: 10,
  });

  for (const state of states) {
    const logs = await getRecentLogs(userId, state.problemId);
    const pCorrect = calculatePCorrectFromLogs(logs, now);

    if (pCorrect > EASY_THRESHOLD) {
      return {
        problemId: state.problemId,
        problemText: state.problem.problemText,
        groupId: state.problem.groupId,
        type: "easy",
        pCorrect,
        box: state.box,
      };
    }
  }

  // Fallback: any problem in box 2+
  if (states.length > 0) {
    const state = states[0];
    const logs = await getRecentLogs(userId, state.problemId);
    const pCorrect = calculatePCorrectFromLogs(logs, now);

    return {
      problemId: state.problemId,
      problemText: state.problem.problemText,
      groupId: state.problem.groupId,
      type: "easy",
      pCorrect,
      box: state.box,
    };
  }

  return null;
}

/**
 * Get recent logs for p_correct calculation
 */
async function getRecentLogs(
  userId: string,
  problemId: string
): Promise<Array<{ timestamp: Date; wasCorrect: boolean }>> {
  const logs = await prisma.reviewLog.findMany({
    where: { userId, problemId },
    orderBy: { timestamp: "desc" },
    take: 10,
    select: { timestamp: true, wasCorrect: true },
  });

  return logs;
}
